import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import FooterSection from '../components/Footer';
import ContactIcon from '../components/ContactIcon';
import { useLanguage } from '../contexts/LanguageContext';
import './css/servicedetail.css';
import lined from '../components/images/hero/lined.png';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadService();
    window.scrollTo(0, 0);
  }, [id]);

  const loadService = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://server.acpca.net/api';
      const response = await fetch(`${apiUrl}/services/${id}`);
      const data = await response.json();
      if (data.success) {
        console.log('Service data loaded:', data.data); // Debug log
        setService(data.data);
      } else {
        console.error('Service not found:', data.message);
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading service:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>جاري التحميل...</p>
        </div>
        <FooterSection />
        <ContactIcon />
      </>
    );
  }

  if (!service) {
    return null;
  }

  const serviceTitle = language === 'ar' ? service.title_ar : service.title_en;
  const serviceDescription = service.detail 
    ? (language === 'ar' ? service.detail.description_ar : service.detail.description_en)
    : (language === 'ar' ? service.description_ar : service.description_en);
  const serviceImage = service.detail?.detail_image_url || service.image_url;
  const features = service.features || [];
  const gallery = service.gallery || [];

  return (
    <>
      <Navbar />
      <section className="service-detail">
        <img src={lined} alt="service-detail" className="service-detail-lined" />
        
        {/* Breadcrumb */}
        <div className="service-detail-breadcrumb">
          <div className="container">
            <motion.nav 
              className="breadcrumb-nav"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/">الرئيسية</Link>
              <span>/</span>
              <Link to="/#services">الخدمات</Link>
              <span>/</span>
              <span>{serviceTitle}</span>
            </motion.nav>
          </div>
        </div>
        
        {/* Hero Section */}
        <div className="service-detail-hero">
          <div className="service-detail-hero-overlay"></div>
          <div 
            className="service-detail-hero-bg"
            style={{ backgroundImage: `url(${serviceImage})` }}
          ></div>
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <motion.div 
                  className="service-detail-hero-content"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  {service.icon && (
                    <div className="service-detail-icon">
                      <i className={service.icon}></i>
                    </div>
                  )}
                  <h1>{serviceTitle}</h1>
                  <p>{serviceDescription}</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="service-detail-features">
          <div className="container">
            <motion.div 
              className="service-detail-section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>المميزات والخدمات</h2>
              <span className="section-divider"></span>
            </motion.div>
            <div className="row">
              {features.length > 0 ? (
                features.map((feature, index) => (
                  <div key={feature.id || index} className="col-lg-4 col-md-6 col-12">
                    <motion.div 
                      className="feature-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: index * 0.1 }}
                    >
                      <div className="feature-icon">
                        <i className="las la-check-circle"></i>
                      </div>
                      <h4>{language === 'ar' ? feature.feature_ar : feature.feature_en}</h4>
                    </motion.div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {language === 'ar' ? 'لا توجد مميزات متاحة' : 'No features available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="service-detail-gallery">
          <div className="container">
            <motion.div 
              className="service-detail-section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>معرض الصور</h2>
              <span className="section-divider"></span>
            </motion.div>
            <div className="row">
              {gallery.length > 0 ? (
                gallery.map((img, index) => (
                  <div key={img.id || index} className="col-lg-4 col-md-6 col-12">
                    <motion.div 
                      className="gallery-item"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: index * 0.1 }}
                    >
                      <img 
                        src={img.image_url && (img.image_url.startsWith('http') ? img.image_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${img.image_url}`)} 
                        alt={`${serviceTitle} - ${index + 1}`} 
                      />
                    </motion.div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {language === 'ar' ? 'لا توجد صور متاحة' : 'No images available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="service-detail-cta">
          <div className="container">
            <motion.div 
              className="service-detail-cta-content"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>هل تريد معرفة المزيد عن هذه الخدمة؟</h2>
              <p>تواصل معنا الآن واحصل على استشارة مجانية</p>
              <a 
                href="https://wa.me/96600000000" 
                target="_blank" 
                rel="noopener noreferrer"
                className="service-detail-cta-btn"
              >
                <i className="lab la-whatsapp"></i>
                تواصل معنا عبر واتساب
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      <FooterSection />
      <ContactIcon />
    </>
  );
};

export default ServiceDetail;

